import { forwardRef, type CSSProperties, type SVGAttributes } from 'react';

import { cn } from '../utils/cn';

import {
  KOLEJNOSC_SKLADANIA,
  OBRECZE_SYGNETU,
  ODLAMKI,
  PROMIEN_RDZENIA,
  SIATKA_SYGNETU,
  SRODEK_SYGNETU,
  UKLADY_LOGO,
  sciezkaKola,
  sciezkaWycinka,
  startSkladania,
} from './geometria';
import { SCIEZKA_NAPISU } from './napis';

/**
 * Sygnet żywy — znak Insolitum w ruchu. Ruch żyje w CSS
 * (`styles/marka.css`, klasy `.ids-sygnet*`); komponent rozkłada znak na
 * rdzeń i obręcze i mówi, który tryb gra.
 *
 * Symbolika: rdzeń to żywa sieć i Nexus, odłamki obręczy to organy.
 *
 * | tryb        | znaczenie                                  | gdzie                                  |
 * | ----------- | ------------------------------------------ | -------------------------------------- |
 * | `praca`     | sieć pracuje: impuls z rdzenia do organów  | ładowanie, bramka organu, aktualizacja |
 * | `oddech`    | czuwanie: czekamy na człowieka/zdarzenie   | parowanie tablicy i telefonu           |
 * | `skladanie` | organy dołączają do sieci (raz)            | wejście w logowanie, aktywacja         |
 * | `puls`      | sygnał doszedł (raz)                       | sparowano, połączenie wróciło          |
 * | `spoczynek` | znak wzorcowy                              | wszystko, co stoi                      |
 *
 * Każdy tryb KOŃCZY na znaku wzorcowym — także pod `prefers-reduced-motion`
 * (globalna reguła Aurora skraca animację do ostatniej klatki). `skladanie`
 * bierze stan końcowy ze STYLU STATYCZNEGO (obręcze widoczne, warstwa
 * organów schowana), a animacja tylko go zasłania (`fill-mode: backwards`):
 * przełączenie `step-end` z `both` zależało od tego, czy postęp doszedł do
 * 1, a Chromium zatrzymuje go na 0,9999999999999994 — zostawało osiem
 * organów ze szwami zamiast znaku wzorcowego. Znak nigdy
 * nie jest obracany: README pakietu — cięcia obręczy mają stałe położenie.
 * Ruch to skala, krycie i przesunięcie; obracają się wyłącznie pojedyncze
 * odłamki w drodze do swojego miejsca.
 *
 * `uklad="pionowe"` rysuje układ pionowy: sygnet żyje, napis stoi (napis
 * nigdy się nie rusza).
 *
 * Komponent jest serwerowy (bez hooków).
 */
export type TrybSygnetu = 'praca' | 'oddech' | 'skladanie' | 'puls' | 'spoczynek';

export interface SygnetZywyProps extends Omit<
  SVGAttributes<SVGSVGElement>,
  'width' | 'height' | 'viewBox' | 'children'
> {
  tryb?: TrybSygnetu;
  uklad?: 'sygnet' | 'pionowe';
  /** Wysokość w pikselach. Bez niej rozmiar ustala klasa CSS. */
  rozmiar?: number;
  /**
   * Komunikat dla czytnika ekranu. Podany → `role="status"` (np. „Wczytywanie
   * organu Agenda"). Bez niego znak jest dekoracyjny.
   */
  etykieta?: string;
}

const RDZEN = sciezkaKola(SRODEK_SYGNETU, SRODEK_SYGNETU, PROMIEN_RDZENIA);
const [SRODKOWA, ZEWNETRZNA_GORA, ZEWNETRZNA_DOL] = OBRECZE_SYGNETU.map((w) => sciezkaWycinka(w));

const liczba = (n: number): string => String(Math.round(n * 1000) / 1000);

function Czesci({ tryb }: { tryb: TrybSygnetu }) {
  return (
    <>
      <path className="ids-sygnet__rdzen" d={RDZEN} />
      <g className="ids-sygnet__obrecze">
        <path className="ids-sygnet__obrecz ids-sygnet__obrecz--srodkowa" d={SRODKOWA} />
        <g className="ids-sygnet__obrecz ids-sygnet__obrecz--zewnetrzna">
          <path d={ZEWNETRZNA_GORA} />
          <path d={ZEWNETRZNA_DOL} />
        </g>
      </g>
      {tryb === 'skladanie' ? (
        // Organy w drodze do sieci. Po ostatnim znika ta warstwa, a pojawiają
        // się obręcze wzorcowe — w spoczynku znak ma zawsze cztery ścieżki,
        // bez włosowych szwów między sąsiadami.
        <g className="ids-sygnet__organy">
          {ODLAMKI.map((o) => {
            const s = startSkladania(o);
            const kat = (((o.od + o.do) / 2) * Math.PI) / 180;
            return (
              <g
                key={o.id}
                className="ids-sygnet__organ"
                style={
                  {
                    '--sx': `${liczba(s.odsuniecie * Math.cos(kat))}px`,
                    '--sy': `${liczba(s.odsuniecie * Math.sin(kat))}px`,
                    '--sobrot': `${liczba(s.obrot)}deg`,
                    '--i': KOLEJNOSC_SKLADANIA[o.id] ?? 1,
                  } as CSSProperties
                }
              >
                <path d={sciezkaWycinka(o)} />
              </g>
            );
          })}
        </g>
      ) : null}
    </>
  );
}

export const SygnetZywy = forwardRef<SVGSVGElement, SygnetZywyProps>(
  ({ tryb = 'praca', uklad = 'sygnet', rozmiar, etykieta, className, ...props }, ref) => {
    const dostepnosc = {
      role: etykieta ? 'status' : undefined,
      'aria-label': etykieta,
      'aria-hidden': etykieta ? undefined : true,
    } as const;

    if (uklad === 'pionowe') {
      const u = UKLADY_LOGO.pionowe;
      return (
        <svg
          ref={ref}
          xmlns="http://www.w3.org/2000/svg"
          viewBox={`0 0 ${u.szerokosc} ${u.wysokosc}`}
          height={rozmiar}
          width={rozmiar !== undefined ? (rozmiar * u.szerokosc) / u.wysokosc : undefined}
          fill="currentColor"
          focusable="false"
          className={cn('ids-logo', 'ids-logo--pionowe', className)}
          style={{ aspectRatio: `${u.szerokosc} / ${u.wysokosc}` }}
          {...dostepnosc}
          {...props}
        >
          <svg
            x={u.sygnet.x}
            y={u.sygnet.y}
            width={SIATKA_SYGNETU}
            height={SIATKA_SYGNETU}
            viewBox={`0 0 ${SIATKA_SYGNETU} ${SIATKA_SYGNETU}`}
            overflow="visible"
            className="ids-sygnet"
            data-tryb={tryb}
          >
            <Czesci tryb={tryb} />
          </svg>
          <path
            className="ids-logo__napis"
            d={SCIEZKA_NAPISU}
            transform={`translate(${u.napis.x} ${u.napis.y})`}
          />
        </svg>
      );
    }

    return (
      <svg
        ref={ref}
        xmlns="http://www.w3.org/2000/svg"
        viewBox={`0 0 ${SIATKA_SYGNETU} ${SIATKA_SYGNETU}`}
        width={rozmiar}
        height={rozmiar}
        fill="currentColor"
        focusable="false"
        className={cn('ids-sygnet', className)}
        data-tryb={tryb}
        {...dostepnosc}
        {...props}
      >
        <Czesci tryb={tryb} />
      </svg>
    );
  }
);

SygnetZywy.displayName = 'SygnetZywy';
